import { wrapReducer } from "@client/content/lib/helper";
import { type FailureActionData } from "@client/lib/actions";
import { Status } from "@client/types";
import { type ReduxAction } from "@client/types/ReduxAction";
import {
  Actions,
  type AddPostCommmentSuccessActionData,
  type AddPostSuccessActionData,
  type InitSuccessActionData,
  type LoadPostCommentsPageBeginActionData,
  type LoadPostCommentsPageSuccessActionData,
  type LoadPostPageBeginActionData,
  type LoadPostPageSuccessActionData,
  type RemovePostCommentBeginActionData,
  type SetActiveActionActionData,
  type SetShowingPostIdActionData,
  type UpdatePostBeginActionData,
  type UpdatePostCommentBeginActionData,
  type UpdatePostSuccessActionData
} from "./actions";
import { type Forums } from "./types";

const reducer = (state: Forums.AppState, action: ReduxAction<any>): Forums.AppState => {
  switch (action.type) {
    case Actions.InitBegin: {
      const { page, forumId } = action.data as LoadPostPageBeginActionData;
      return {
        ...state,
        forumId,
        page,
        status: Status.Loading
      };
    }
    case Actions.InitFailure: {
      return {
        ...state,
        status: Status.Failed
      };
    }
    case Actions.InitSuccess: {
      const { forumOptions, posts } = action.data as InitSuccessActionData;
      return {
        ...state,
        forumOptions,
        posts: posts,
        status: Status.Ready
      };
    }

    case Actions.LoadPostPageBegin: {
      const { page, forumId, size, search } = action.data as LoadPostPageBeginActionData;
      return {
        ...state,
        forumId,
        page,
        size,
        search,
        status: "LoadingPosts"
      };
    }
    case Actions.LoadPostPageFailure: {
      return {
        ...state,
        posts: [],
        status: Status.Failed
      };
    }
    case Actions.LoadPostPageSuccess: {
      const posts = action.data as LoadPostPageSuccessActionData;
      return {
        ...state,
        posts,
        status: Status.Ready
      };
    }

    case Actions.LoadPostCommentsPageBegin: {
      const { page } = action.data as LoadPostCommentsPageBeginActionData;
      return {
        ...state,
        commentPage: page,
        status: "LoadingComments"
      };
    }
    case Actions.LoadPostCommentsPageFailure: {
      return {
        ...state,
        posts: [],
        status: Status.Failed
      };
    }
    case Actions.LoadPostCommentsPageSuccess: {
      const comments = action.data as LoadPostCommentsPageSuccessActionData;
      return {
        ...state,
        comments,
        status: Status.Ready
      };
    }

    case Actions.AddPostBegin: {
      return {
        ...state,
        status: Status.Saving
      };
    }
    case Actions.AddPostFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        error,
        status: Status.Failed
      };
    }
    case Actions.AddPostSuccess: {
      const item = action.data as AddPostSuccessActionData;
      return {
        ...state,
        posts: [item, ...state.posts],
        status: Status.Ready
      };
    }

    case Actions.UpdatePostBegin: {
      return {
        ...state,
        status: Status.Saving
      };
    }
    case Actions.UpdatePostFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        error,
        status: Status.Failed
      };
    }
    case Actions.UpdatePostSuccess: {
      const params = action.params as UpdatePostBeginActionData;
      const item = action.data as UpdatePostSuccessActionData;
      return {
        ...state,
        posts: state.posts.map((x) => (x.ticketId === params.data.ticketId ? item : x)),
        status: Status.Ready
      };
    }

    case Actions.AddPostCommentBegin: {
      return {
        ...state,
        status: Status.Saving
      };
    }
    case Actions.AddPostCommentFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        error,
        status: Status.Failed
      };
    }
    case Actions.AddPostCommentSuccess: {
      const item = action.data as AddPostCommmentSuccessActionData;
      return {
        ...state,
        comments: [...state.comments, item],
        lastAddedCommentId: item.ticketCommentId,
        status: Status.Ready
      };
    }

    case Actions.UpdatePostCommentBegin: {
      return {
        ...state,
        status: Status.Saving
      };
    }
    case Actions.UpdatePostCommentFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        error,
        status: Status.Failed
      };
    }
    case Actions.UpdatePostCommentSuccess: {
      const params = action.params as UpdatePostCommentBeginActionData;
      const item = action.data as AddPostCommmentSuccessActionData;
      return {
        ...state,
        comments: state.comments.map((x) => (x.ticketCommentId === params.data.ticketCommentId ? item : x)),
        status: Status.Ready
      };
    }

    case Actions.RemovePostCommentSuccess: {
      const { ticketCommentId } = action.params as RemovePostCommentBeginActionData;
      return {
        ...state,
        comments: state.comments.filter(
          (x) => x.ticketCommentId !== ticketCommentId && x.parentTicketCommentId !== ticketCommentId
        )
      };
    }

    case Actions.RemovePostSuccess: {
      const { ticketId } = action.params as RemovePostCommentBeginActionData;
      return {
        ...state,
        posts: state.posts.filter((x) => x.ticketId !== ticketId)
      };
    }

    case Actions.SetActiveAction: {
      const data = action.data as SetActiveActionActionData;
      return {
        ...state,
        activeAction: data.action
      };
    }

    case Actions.SetShowingPostId: {
      const { ticketId: itemId } = action.data as SetShowingPostIdActionData;
      return {
        ...state,
        showingPostId: itemId
      };
    }

    default:
      return state;
  }
};

export const defaultState: Forums.AppState = {
  activeAction: { target: "post", targetId: "", type: "reply" },
  commentPage: 1,
  comments: [],
  error: null,
  forumId: null,
  forumOptions: [],
  lastAddedCommentId: null,
  page: 1,
  size: 10,
  id: "",
  status: Status.None,
  posts: [],
  search: null,
  showingPostId: undefined
};
export const previewState: Forums.AppState = {
  ...defaultState,
  status: Status.Ready,
  posts: [
    {
      agentFirstName: "Dummy",
      agentLastName: "Dummy",
      approvedTicketCommentCount: 1,
      contactFirstName: "Dummy",
      contactHasRead: true,
      contactLastName: "Dummy",
      contactProfilePictureSourceId: null,
      contactProfilePictureSourceKey: null,
      contactUserId: 10,
      createdOnDate: new Date().toISOString(),
      forumColor: "#eee",
      forumId: "",
      forumName: "Dummy",
      isApproved: true,
      lastModifiedOnDate: new Date().toISOString(),
      message: "Dummy",
      number: 1,
      priorityModifiedOnDate: new Date().toISOString(),
      subject: "Dummy",
      ticketId: "",
      ticketStatusKey: "Active",
      ticketStatusModifiedOnDate: new Date().toISOString(),
      ticketStatusName: "Dummy",
      ticketTypeModifiedOnDate: new Date().toISOString(),
      tierModifiedOnDate: new Date().toISOString(),
      totalTicketCommentCount: 10
    }
  ]
};
export default wrapReducer({ defaultState, reducer, previewState });
